'use client';

import { ArrayElement, GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { ProviderType } from '@/reporting';
import { useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import { font } from '@/theme/zeelool.theme';
import { env } from '@/utils/env';
import { EPlatform } from '@/utils/enums/platform-enum';
import { setSession } from '@/utils/tools';
import {
  buildSpmPositionCode,
  padSpmPositionIndex,
  useGlobalSpmTerminal,
} from '@/components/Layout/spmTerminal';
import { Collapse, List, ListItemButton, ListItemText } from '@mui/material';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { cloneElement, type ReactElement, useState } from 'react';
import { LEGAL_LINKS_TEMPLATE_ID } from '../2026-pc/legalLinks';
import { PAYMENT_TEMPLATE_ID } from '../2026-pc/paymentModules';
import { COPYRIGHT_TEMPLATE_ID } from '../2026-pc/copyrightModules';

type FooterNavItem = ArrayElement<GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>>;

interface FooterNavContent {
  layout_title: string;
  /** 运营后台可能未配置跳转链接（如 popup 类型项），此处允许为空。 */
  list: Array<{ title: string; id: string; href?: string | null }>;
}

/**
 * 循环内挂载点 Data SPM DOM 打标：hook 不能在 map 中调用，封为小组件把 data-spm
 * 克隆到唯一真实子元素上（不新增 DOM 层级）；开关关闭或编码表外时原样渲染，零 DOM 差异。
 */
function SpmDomNode({ code, children }: { code: string | undefined; children: ReactElement }) {
  const domSpm = useSpmDomAttr(code);
  return domSpm ? cloneElement(children, domSpm) : children;
}

const NavList = ({
  navList,
}: {
  navList: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
}) => {
  const [openItemId, setOpenItemId] = useState<number | null>(null);
  // footer_navigation.footer_link_XX 为跨分组的扁平序号，先按分组累计偏移。
  let runningLinkCount = 0;
  const groupLinkOffsets = navList.map((item) => {
    const list = (item.content as FooterNavContent)?.list;
    const offset = runningLinkCount;
    runningLinkCount += Array.isArray(list) ? list.length : 0;
    return offset;
  });

  const handleNavItemToggle = (itemId: number) => {
    setOpenItemId((prevOpenId) => (prevOpenId === itemId ? null : itemId));
  };

  return (
    <List
      component="nav"
      sx={{
        width: '100%',
        padding: '0 0 8px',
        '& .MuiListItemText-root': {
          margin: 0,
        },
        '& .footer-nav-trigger': {
          minHeight: '48px',
          padding: 0,
        },
        '& .footer-nav-trigger .MuiTypography-root': {
          color: '#fff',
          fontFamily: font.bs,
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: 1,
        },
        '& .footer-nav-link': {
          minHeight: '40px',
          padding: 0,
        },
        '& .footer-nav-link .MuiTypography-root': {
          color: '#fff',
          fontFamily: font.br,
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: '14px',
        },
      }}
    >
      {navList.map((item, firstLevelIndex) => {
        // 专用模板在 Footer 底部展示，保留普通导航在原响应中的埋点索引。
        if (
          item.template_id === LEGAL_LINKS_TEMPLATE_ID ||
          item.template_id === PAYMENT_TEMPLATE_ID ||
          item.template_id === COPYRIGHT_TEMPLATE_ID
        )
          return null;
        return (
          <NavItemCollapse
            key={item.id}
            navItem={item}
            firstLevelIndex={firstLevelIndex}
            groupLinkOffset={groupLinkOffsets[firstLevelIndex]}
            isOpen={openItemId === item.id}
            onToggle={() => handleNavItemToggle(item.id!)}
          />
        );
      })}
    </List>
  );
};

const NavItemCollapse = ({
  navItem,
  firstLevelIndex,
  groupLinkOffset,
  isOpen,
  onToggle,
}: {
  navItem: FooterNavItem;
  firstLevelIndex: number;
  /** 本分组链接在 footer_navigation.footer_link_XX 扁平序号中的起始偏移。 */
  groupLinkOffset: number;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const { trackEvent } = useReporting();
  const router = useRouter();
  const { cnName } = useRouteMetadata();
  // 全局组件的 B 位跟随当前页面，C/D 沿用全局终端模板。
  const spmPage = useGlobalSpmTerminal(EPlatform.WAP);
  // Data SPM DOM 打标：与分组展开/收起、链接点击上报的坑位同源同值。
  const groupToggleDomSpm = useSpmDomAttr(
    buildSpmPositionCode(
      spmPage,
      'footer_navigation',
      `footer_group_toggle_${padSpmPositionIndex(firstLevelIndex + 1)}`,
    ),
  );
  const content = navItem.content as FooterNavContent;

  if ((navItem.template_id != null && navItem.template_id !== 1) || !Array.isArray(content?.list))
    return null;

  const handleClickNavItem = (
    event: React.MouseEvent<HTMLAnchorElement>,
    item: { title: string; id: string; href?: string | null },
    secondLevelIndex: number,
  ) => {
    event.preventDefault();
    const itemHref = item.href || '';
    // 底部导航按坑位序号规则出码（footer_link_XX = 跨分组扁平序号），表快照仅作核验基线。
    const footerLinkSpmCnt = buildSpmPositionCode(
      spmPage,
      'footer_navigation',
      `footer_link_${padSpmPositionIndex(groupLinkOffset + secondLevelIndex + 1)}`,
    );
    const linkProperties = {
      page_name: cnName,
      url_path: window.location.href,
      first_tab_name: content.layout_title,
      second_tab_name: item.title,
      first_tab_index: firstLevelIndex,
      second_tab_index: secondLevelIndex,
    };
    trackEvent(
      {
        eventName: 'BottomNavTab_Click',
        properties: withSpmCnt(linkProperties, footerLinkSpmCnt),
      },
      [ProviderType.SENSORS],
    );
    // 无链接时只上报，不做跳转。
    if (!itemHref) {
      return;
    }
    if (itemHref.includes('goods-list')) {
      setSession('zl_goodsListEnterPageScene', '全局导航');
    }
    if (itemHref === 'https://cookie.zeelool.com') {
      window.OneTrust.ToggleInfoDisplay();
    } else if (
      (itemHref.includes('zeelool.com') || itemHref.includes('xmslol.cn')) &&
      !itemHref.includes(env.NEXT_PUBLIC_HELP_CENTER_PATH)
    ) {
      const href = new URL(itemHref);
      router.push(href.href.replace(href.origin, ''));
    } else {
      window.location.href = itemHref;
    }
  };

  return (
    <div className="border-b border-solid border-[#808080] last:border-b-0">
      <ListItemButton
        className="footer-nav-trigger"
        aria-expanded={isOpen}
        onClick={() => {
          onToggle();
          // 分组展开/收起按坑位序号规则出码（footer_group_toggle_XX = firstLevelIndex + 1）。
          const groupToggleSpmCnt = buildSpmPositionCode(
            spmPage,
            'footer_navigation',
            `footer_group_toggle_${padSpmPositionIndex(firstLevelIndex + 1)}`,
          );
          const toggleProperties = {
            first_tab_name: content.layout_title,
            first_tab_index: firstLevelIndex,
            page_name: cnName,
            url_path: window.location.href,
          };
          trackEvent(
            {
              eventName: 'BottomNavTab_Click',
              properties: withSpmCnt(toggleProperties, groupToggleSpmCnt),
            },
            [ProviderType.SENSORS],
          );
        }}
        {...groupToggleDomSpm}
      >
        <ListItemText primary={content.layout_title} />
        <CollapseArrowDownOutlined
          className={`size-[18px] text-primary-white transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
          aria-hidden
        />
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {content.list.map((item, index) =>
            typeof item?.href === 'string' && item.href.trim() ? (
              <ListItemButton className="footer-nav-link" key={`${item.href}-${index}`}>
                <ListItemText>
                  <SpmDomNode
                    code={buildSpmPositionCode(
                      spmPage,
                      'footer_navigation',
                      `footer_link_${padSpmPositionIndex(groupLinkOffset + index + 1)}`,
                    )}
                  >
                    <Link
                      href={item.href}
                      onClick={(event) => handleClickNavItem(event, item, index)}
                    >
                      {item.title}
                    </Link>
                  </SpmDomNode>
                </ListItemText>
              </ListItemButton>
            ) : null,
          )}
        </List>
      </Collapse>
    </div>
  );
};

export default NavList;
